import dayjs from "dayjs";
import * as XLSX from "xlsx";

export interface ColumnMeta {
  title: string;
  type: string;
  description: string;
  order: string;
}

export const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

export const cleanNumber = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  return parseFloat(String(value).replace(/[£$€%, ]/g, "")) || 0;
};

export const isNumeric = (v: any): boolean => {
  const cleaned = String(v).replace(/[£$€%, ]/g, "");
  return cleaned !== "" && !isNaN(parseFloat(cleaned));
};

export const PERCENT_STYLES = {
  red: { bg: "#FDEDED", color: "#D32F2F", border: "#D32F2F80" },
  yellow: { bg: "#FFFAEB", color: "#DC6803", border: "#FFC593" },
  green: { bg: "#EDF7ED", color: "#2E7D32", border: "#2E7D3280" },
} as const;

export const getPercentStyle = (percent: number) => {
  if (Number.isNaN(percent)) return PERCENT_STYLES.green;
  if (percent <= 12) return PERCENT_STYLES.red;
  if (percent <= 20) return PERCENT_STYLES.yellow;
  return PERCENT_STYLES.green;
};

export const formatInt = (v: unknown) =>
  cleanNumber(v).toLocaleString("en-US", { maximumFractionDigits: 0 });

export const formatCurrency = (v: unknown, symbol: string) => {
  const num = cleanNumber(v);
  const abs = Math.abs(num).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  return num < 0 ? `-${symbol}${abs}` : `${symbol}${abs}`;
};

/* ----------------------------------------------------------------------------
 * Reusable cell formatters for column `format` functions. Used by BOTH the
 * table render and the export, so they stay identical. Empty/null returns ""
 * (blank cell); tables show "N/A"/"" via their own renderCell guard.
 * ------------------------------------------------------------------------- */

// e.g. 1055 -> "$1,055", -1055 -> "-$1,055". Pass { minDecimals: 2 } to force cents.
export const formatUsd = (
  value: any,
  opts?: { minDecimals?: number; maxDecimals?: number },
): string => {
  if (!isNumeric(value)) return value == null ? "" : String(value);
  const num = cleanNumber(value);
  const sign = num < 0 ? "-" : "";
  const abs = Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: opts?.minDecimals ?? 0,
    maximumFractionDigits: opts?.maxDecimals ?? 2,
  });
  return `${sign}$${abs}`;
};

// e.g. 41 -> "41%"
export const formatPercent = (value: any): string =>
  isNumeric(value)
    ? `${cleanNumber(value)}%`
    : value == null
      ? ""
      : String(value);

// month bucket -> "Jul 2026"
export const formatMonth = (value: any): string => {
  if (value == null || value === "") return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("MMM YYYY") : String(value);
};

// full date -> "21/04/2026"
export const formatDate = (value: any): string => {
  if (value == null || value === "") return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : String(value);
};

/* ============================================================================
 * XLSX EXPORT — generic, driven by the column config the PARENT passes in.
 *
 * Each Anomalies table exports its own ordered `ExportColumn[]` (key + label,
 * optional per-column `format`). The parent imports that list and passes it
 * with the rows, so every tab exports exactly its own columns/order.
 *
 * XLSX (not CSV) so £/€/accented text can't be mis-decoded: a CSV has no
 * reliable place to declare its encoding, so some readers show "£" as "Â£";
 * an .xlsx stores strings as UTF-8 in its XML, removing the ambiguity.
 * ========================================================================== */

export interface ExportColumn {
  key: string;
  label: string;
  /** present in the export only; hidden from the on-screen table */
  exportOnly?: boolean;
  format?: (value: any, row: any) => string;
}

// Matches the `=HYPERLINK("url","label")` strings the link columns emit, so we
// can write real clickable Excel hyperlinks instead of literal formula text.
// Inner quotes are doubled ("") by the formatter; we un-double them here.
const HYPERLINK_RE = /^=HYPERLINK\("(.+?)","(.+?)"\)$/;

export const exportRowsToXlsx = (
  rows: any[],
  columns: ExportColumn[],
  filename = "export.xlsx",
) => {
  if (!rows?.length || !columns?.length) return;

  const aoa: string[][] = [
    columns.map((c) => c.label),
    ...rows.map((row) =>
      columns.map((c) => {
        const value = row?.[c.key];
        return c.format
          ? c.format(value, row)
          : value == null
            ? ""
            : String(value);
      }),
    ),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Convert "=HYPERLINK(...)" text cells into native clickable hyperlinks.
  Object.keys(ws).forEach((addr) => {
    if (addr[0] === "!") return;
    const cell = ws[addr] as any;
    if (typeof cell.v === "string") {
      const m = cell.v.match(HYPERLINK_RE);
      if (m) {
        const url = m[1].replace(/""/g, '"');
        const label = m[2].replace(/""/g, '"');
        cell.v = label;
        cell.t = "s";
        cell.l = { Target: url, Tooltip: url };
      }
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Export");

  // Accept legacy ".csv" names and normalise the extension to ".xlsx".
  const outName = filename.replace(/\.(csv|xlsx)$/i, "") + ".xlsx";
  XLSX.writeFile(wb, outName);
};

export const exportRowsToCsv = exportRowsToXlsx;
